/**
 * File utility functions for handling file operations.
 *
 * Provides utilities for converting files to base64, data URLs, and
 * extracting base64 from data URLs.
 *
 * @module fileUtils
 */

/**
 * Convert a File object to base64 string (without data URL prefix)
 *
 * Used for uploading image and audio files to the server.
 * Strips the data URL prefix to return pure base64 string.
 *
 * @param file - File to convert
 * @returns Promise resolving to base64 encoded file content
 * @throws Error if file reading fails or no data is found
 */
export const convertFileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;

      if (!result || typeof result !== 'string') {
        reject(new Error('Failed to read file'));
        return;
      }

      // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
      const base64 = result.split(',')[1];

      if (!base64) {
        reject(new Error('No base64 data found'));
        return;
      }

      resolve(base64);
    };
    reader.onerror = () => reject(new Error(`File reading failed: ${reader.error?.message || 'Unknown error'}`));
  });
};

/**
 * Convert a File object to data URL (for preview purposes)
 *
 * Used for creating preview URLs for image/audio files in the UI.
 * Returns the full data URL including the prefix.
 *
 * @param file - File to convert
 * @returns Promise resolving to data URL string
 * @throws Error if file reading fails
 */
export const convertFileToDataURL = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      if (!result || typeof result !== 'string') {
        reject(new Error('Failed to read file'));
        return;
      }
      resolve(result);
    };
    reader.onerror = () => reject(new Error(`File reading failed: ${reader.error?.message || 'Unknown error'}`));
  });
};

/**
 * Convert a Blob to data URL (for preview purposes)
 *
 * Used for converting Blob objects (e.g., from fetch responses) to data URLs.
 *
 * @param blob - Blob to convert
 * @returns Promise resolving to data URL string
 * @throws Error if blob reading fails
 */
export const convertBlobToDataURL = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onload = () => {
      const result = reader.result as string;
      if (!result || typeof result !== 'string') {
        reject(new Error('Failed to read blob'));
        return;
      }
      resolve(result);
    };
    reader.onerror = () => reject(new Error(`File reading failed: ${reader.error?.message || 'Unknown error'}`));
  });
};

/**
 * Extract base64 string from a data URL
 *
 * Helper function to extract the base64 portion from a data URL.
 *
 * @param dataURL - Data URL string
 * @returns Base64 string without the prefix
 * @throws Error if data URL format is invalid
 */
export const extractBase64FromDataURL = (dataURL: string): string => {
  const parts = dataURL.split(',');
  if (parts.length < 2) {
    throw new Error('Invalid data URL format');
  }
  return parts[1];
};
